import React, { useEffect, useState } from 'react';
import { Card, Form, Switch, Input, Checkbox, Button, Typography, message, Space } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { ShopSettings } from '../../types';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * The placeholder value `shop_settings.company_address` carries until an
 * owner edits Shop Settings (see `backend/src/database/migrate.ts`'s
 * `company_address TEXT DEFAULT '123 Main Street'`). Receipts email
 * customers, so the switch stays off — and disabled — until that's real.
 */
const PLACEHOLDER_ADDRESS = '123 Main Street';

/**
 * Whether the shop address is filled in for real. The check used to be
 * "empty, or exactly the seeded placeholder" — which let anything else through,
 * including the single '.' found sitting in production. A receipt is a document
 * a customer keeps; one printing '.' where the shop's address belongs is worse
 * than one that was never sent. Anything too short to be an address counts as
 * not filled in.
 */
function isRealAddress(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  if (!trimmed || trimmed === PLACEHOLDER_ADDRESS) return false;
  // Shortest plausible real address still has a number and a street name.
  return trimmed.replace(/[^A-Za-z0-9]/g, '').length >= 5;
}

interface BlockToggles {
  thankYou: boolean;
  authorization: boolean;
  warranty: boolean;
  banner: boolean;
  disclaimer: boolean;
}

const DEFAULT_BLOCKS: BlockToggles = {
  thankYou: true,
  authorization: true,
  warranty: true,
  banner: true,
  disclaimer: true,
};

// Only the blocks receiptHtml.ts actually reads belong here. `authorization`
// and `banner` stay in BlockToggles/DEFAULT_BLOCKS (and round-trip through
// receipt_blocks_json unchanged, defaulted true) so a future task can wire a
// checkbox for them next to the code that finally reads them — but showing
// one now would let an owner toggle a control that visibly does nothing:
// authorization always follows whether the estimate was signed
// (receiptHtml.ts), and the banner is chosen per-promotion rather than here —
// ReceiptDelivery renders whichever promotion the receipt was issued with.
const BLOCK_OPTIONS: { label: string; value: keyof BlockToggles }[] = [
  { label: 'Thank-you message', value: 'thankYou' },
  { label: 'Warranty', value: 'warranty' },
  { label: 'Estimate disclaimer', value: 'disclaimer' },
];

function parseBlocks(json: string | null | undefined): BlockToggles {
  if (!json) return DEFAULT_BLOCKS;
  try {
    const parsed = JSON.parse(json);
    return { ...DEFAULT_BLOCKS, ...parsed };
  } catch {
    return DEFAULT_BLOCKS;
  }
}

function blocksToKeys(blocks: BlockToggles): (keyof BlockToggles)[] {
  return (Object.keys(blocks) as (keyof BlockToggles)[]).filter((key) => blocks[key]);
}

const ReceiptSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addressIsPlaceholder, setAddressIsPlaceholder] = useState(true);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<ShopSettings>('/shop-settings');
      const placeholder = !isRealAddress(data.company_address);
      setAddressIsPlaceholder(placeholder);
      form.setFieldsValue({
        receipts_enabled: !placeholder && Number(data.receipts_enabled) === 1,
        receipt_thank_you: data.receipt_thank_you || '',
        receipt_warranty: data.receipt_warranty || '',
        blocks: blocksToKeys(parseBlocks(data.receipt_blocks_json)),
      });
    } catch (error) {
      console.error('Error loading receipt settings:', error);
      message.error('Failed to load receipt settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const selected: (keyof BlockToggles)[] = values.blocks || [];
      const blocks: BlockToggles = {
        thankYou: selected.includes('thankYou'),
        authorization: selected.includes('authorization'),
        warranty: selected.includes('warranty'),
        banner: selected.includes('banner'),
        disclaimer: selected.includes('disclaimer'),
      };
      await api.put('/shop-settings', {
        receipts_enabled: !addressIsPlaceholder && !!values.receipts_enabled,
        receipt_thank_you: values.receipt_thank_you || '',
        receipt_warranty: values.receipt_warranty || '',
        receipt_blocks_json: JSON.stringify(blocks),
      });
      message.success('Receipt settings saved');
    } catch (error) {
      console.error('Error saving receipt settings:', error);
      message.error('Failed to save receipt settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Receipts</Title>
      <Text type="secondary">
        Thank-you text, warranty, what prints on a receipt, and the promotion it carries
      </Text>

      <Card style={{ marginTop: '24px' }} loading={loading}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label="Issue receipts for every payment"
            name="receipts_enabled"
            valuePropName="checked"
            extra={
              addressIsPlaceholder
                ? 'Please fill in your shop details before enabling receipts — a receipt with a placeholder address should never reach a customer.'
                : "Every settled payment will email the customer a receipt. Turning this off later doesn't withdraw receipts already sent."
            }
          >
            <Switch
              aria-label="Issue receipts for every payment"
              disabled={addressIsPlaceholder}
            />
          </Form.Item>

          <Form.Item
            label="Thank-you message"
            name="receipt_thank_you"
            extra="Use {{first_name}} to insert the customer's first name — the server fills it in when the receipt is generated."
          >
            <TextArea rows={3} placeholder="Thank you for choosing us, {{first_name}}!" />
          </Form.Item>

          <Form.Item
            label="Warranty"
            name="receipt_warranty"
            extra="Printed under the totals on every receipt that has this block enabled. Also supports {{first_name}}."
          >
            <TextArea rows={3} placeholder="12 months / 12,000 miles on parts and labor." />
          </Form.Item>

          <Form.Item
            label="What prints on a receipt"
            name="blocks"
            extra="The estimate disclaimer comes from the estimate itself, not from text typed here."
          >
            <Checkbox.Group options={BLOCK_OPTIONS} />
          </Form.Item>

          <Paragraph type="secondary" style={{ marginTop: -8 }}>
            The promotion carried on receipts is configured with promotions, not here.
          </Paragraph>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ReceiptSettings;
